import { ArrowRight, Clock, FlaskConical, Search, User } from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { cn } from '#client/lib/cn';
import {
  type PaletteRow,
  type PaletteRowKind,
  emptyMessage,
  withGroupHeadings,
} from './palette.logic';
import { usePaletteNavigation, usePaletteRows } from './usePalette';

/**
 * The command palette.
 *
 * ## Why not `cmdk`
 *
 * The standard React implementation pulls in `@radix-ui/react-dialog`, which
 * reimplements focus trapping, Escape handling and the top layer in JavaScript.
 * This app already gets all three from the platform, because its Modal is a
 * native `<dialog>` — `cmdk` is the default precisely for apps that lack a
 * focus-managed dialog primitive, and adding one here would be the project's
 * first Radix dependency in exchange for machinery it already has.
 *
 * ## What it contains
 *
 * Jump-to-record by PIN, MR number, name or phone; a short list of
 * destinations; and recently opened records. Not an exhaustive command
 * registry — a palette that lists everything is a menu with extra steps.
 */
export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  // Drive the native dialog from the `open` prop. showModal() is what provides
  // the focus trap, the top layer and Escape-to-close.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (open && !dialog.open) dialog.showModal();
    else if (!open && dialog.open) dialog.close();
  }, [open]);

  return (
    <dialog
      ref={dialogRef}
      className="c-palette"
      aria-label="Search and commands"
      // Escape fires `cancel`; clicking the backdrop targets the dialog itself.
      onCancel={onClose}
      onClick={(event) => {
        if (event.target === dialogRef.current) onClose();
      }}
    >
      {/*
        The body only exists while the palette is open, so closing it discards
        the query, the results and the highlight by unmounting rather than by
        resetting them from an effect.
      */}
      {open && <PaletteBody onClose={onClose} />}
    </dialog>
  );
}

function PaletteBody({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const [query, setQuery] = useState('');

  // Autofocus on mount, which is also when the palette opens.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const trimmed = query.trim();
  const rows = usePaletteRows(trimmed);

  const handleOpen = (href: string) => {
    onClose();
    void navigate(href);
  };

  const { activeIndex, activeRow, setActive, handleKeyDown } = usePaletteNavigation(
    rows,
    handleOpen,
    listRef,
  );

  return (
    <div className="c-palette__box">
      <div className="c-palette__search">
        <Search aria-hidden="true" />
        <input
          ref={inputRef}
          className="c-palette__input"
          placeholder="Search patients by name or PIN, or jump to a screen…"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setActive(0);
          }}
          onKeyDown={handleKeyDown}
          // A combobox controlling a listbox, so the highlighted option is
          // announced as the user arrows without focus ever leaving the input.
          role="combobox"
          aria-expanded
          aria-controls="palette-list"
          aria-activedescendant={activeRow ? `palette-row-${activeRow.key}` : undefined}
          autoComplete="off"
        />
        <kbd className="c-palette__kbd">Esc</kbd>
      </div>

      <ul className="c-palette__list" id="palette-list" role="listbox" ref={listRef}>
        {rows.length === 0 && <li className="c-palette__empty">{emptyMessage(trimmed)}</li>}

        {withGroupHeadings(rows).map(({ row, startsGroup }, index) => (
          <PaletteOption
            key={row.key}
            row={row}
            startsGroup={startsGroup}
            isActive={index === activeIndex}
            // Pointer users get the same highlight the keyboard drives.
            onHighlight={() => setActive(index)}
            onOpen={() => handleOpen(row.href)}
          />
        ))}
      </ul>

      <div className="c-palette__footer">
        <span>
          <kbd>↑</kbd>
          <kbd>↓</kbd> to move
        </span>
        <span>
          <kbd>↵</kbd> to open
        </span>
      </div>
    </div>
  );
}

const ROW_ICONS: Record<PaletteRowKind, ReactNode> = {
  patient: <User />,
  report: <FlaskConical />,
  'lab-test': <FlaskConical />,
  recent: <Clock />,
  command: <ArrowRight />,
};

interface PaletteOptionProps {
  row: PaletteRow;
  /** The first row of its group, so it carries the group heading. */
  startsGroup: boolean;
  isActive: boolean;
  onHighlight: () => void;
  onOpen: () => void;
}

function PaletteOption({ row, startsGroup, isActive, onHighlight, onOpen }: PaletteOptionProps) {
  return (
    <li>
      {startsGroup && <p className="c-palette__group">{row.group}</p>}
      <div
        id={`palette-row-${row.key}`}
        role="option"
        aria-selected={isActive}
        className={cn('c-palette__row', isActive && 'is-active')}
        onMouseMove={onHighlight}
        onClick={onOpen}
      >
        <span className="c-palette__icon" aria-hidden="true">
          {ROW_ICONS[row.kind]}
        </span>
        <span className="c-palette__label">{row.label}</span>
        {row.detail && <span className="c-palette__detail">{row.detail}</span>}
      </div>
    </li>
  );
}
